<script setup lang="ts">
import {computed, inject, ref} from 'vue';
import {
  formatting,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  LegacyRma,
  LegacyUnidentifiedRmaRow,
  useContext
} from '@hantera/portal-app';
import {PanelContent} from '@hantera/design-system';
import useBackend from '@/composition/useBackend';
import ProductSelector from '../ProductSelector.vue';
import {CatalogSearchHit} from '@/backend/catalog';
import {v4 as uuidv4} from 'uuid';
import Decimal from 'decimal.js';

const props = defineProps<{
  rma: LegacyRma
  row: LegacyUnidentifiedRmaRow
}>();

const context = useContext();
const { currency } = formatting;
const backend = useBackend();
const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));

const images = computed(() => {
  return props.row.images.map(i => context.serverUrl() + '/legacy' + i);
});

const canEditProduct = computed(() => {
  return props.rma.state !== 'Completed';
});

function openImage(url: string) {
  window.open(url, '_blank');
}

function productSelected(value?: CatalogSearchHit) {
  if (!value) {
    return;
  }

  const delivery = order.value.deliveries.find(r => r.state === 'Delivered') || order.value.deliveries.find(r => r.state === 'Shipped');
  if (!delivery) {
    return;
  }

  const newRowId = uuidv4();

  backend.legacyOrders.buildCommandGroup(
    order.value.orderId,
    "Assign product to unidentified RMA item of {rmaNumber} to {productNumber}",
    {
      rmaNumber: props.rma.rmaNumber,
      productNumber: value.productNumber,
    }
  )
    .command(legacyOrderCommands.addRow, {
      deliveryId: delivery.deliveryId,
      newRowId: newRowId,
      articleNumber: value.productNumber,
      quantity: new Decimal(1),
    })
    .command(legacyOrderCommands.upsertRowField, {
      rowId: newRowId,
      fieldKey: 'UnidentifiedProduct',
      fieldValue: true
    })
    .command(legacyOrderCommands.setRmaRowReference, {
      rmaId: props.rma.rmaId,
      rmaRowId: props.row.id,
      newOrderRowId: newRowId
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Identified previously unidentified row from RMA {rmaNumber} as {productNumber}. As a result, a new row and RMA row is added for delivery {deliveryNumber}',
      fields: {
        rmaNumber: props.rma.rmaNumber,
        productNumber: value.productNumber,
        deliveryNumber: delivery.deliveryNumber,
      }
    })
    .send();
}

</script>

<template>
  <PanelContent class="unidentified-rma-row__panel">
    <div class="unidentified-rma-row">
      <div class="unidentified-rma-row__images">
        <img
          v-for="image in images"
          :src="image"
          @click="openImage(image)"
        />
      </div>
      <div class="unidentified-rma-row__identity">
        <div class="unidentified-rma-row__description">{{ row.description }}</div>
        <ProductSelector
          :can-edit="canEditProduct"
          @selected="productSelected"
        />
      </div>
    </div>
  </PanelContent>
</template>

<style>
.unidentified-rma-row__panel {
  margin: 3px 0;
}

.unidentified-rma-row {
  display: flex;
  align-items: center;
}

.unidentified-rma-row__item {
  display: grid;
  grid-template-columns: max-content auto auto auto;
  grid-auto-rows: min-content;
  column-gap: 5px;
  row-gap: 2px;
  white-space: nowrap;

  line-height: 16px;
}

.unidentified-rma-row__images {
  position: relative;
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  gap: 4px;
  margin-right: 8px;
}

.unidentified-rma-row__images img {
  display: block;
  width: 36px;
  height: 36px;
  object-fit: contain;
  cursor: pointer;
}

.unidentified-rma-row__identity {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  grid-column: 2 / span 3;

  display: flex;
  flex-direction: column;
}

.unidentified-rma-row__description {
  font-style: italic;
  margin-left: 5px;
  padding: 3px 0;
}

.unidentified-rma-row__item-number {
  font-weight: bold;
}

.unidentified-rma-row__item-name {
  padding-left: 5px;
  flex-grow: 1;
}


.unidentified-rma-row__unit-price {
  text-align: right;
}

.unidentified-rma-row__refund-amount {
  text-align: right;
  font-weight: bold;
}

.unidentified-rma-row__comment,
.unidentified-rma-row__summary,
.unidentified-rma-row__exchange {
  padding-left: 43px;
}

.unidentified-rma-row__comment {
  font-style: italic;
}

.unidentified-rma-row__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>